<template>
  <div class="animated fadeIn col-xl-10 offset-md-1">
    <b-card header-tag="header">
      <div slot="header"><i class="icon-list"></i> Premium</div>
      <div v-if="loading">
        <div class="sk-circle-fade float-left">
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
          <div class="sk-grid-cube"></div>
        </div>
        <h2 class="float-left ml-4">Loading premium...</h2>
      </div>
      <div v-else>
        <div v-if="!is_patron && !is_entitled">
          <h4 class="subtitle">Get Premium</h4>
          <p>Hey there, It looks like you don't have premium!</p>
          <div class="d-flex flex-column flex-md-row justify-content-center align-items-center gap-3 mt-3">
            <PatreonPremiumButton class="mb-1 mb-md-0 mr-sm-0 mr-md-2" @track="trackPremiumCTA" />
            <DiscordPremiumButton @track="trackPremiumCTA" />
          </div>
        </div>
        <div v-else>
          <h4 class="subtitle">Hello there!</h4>
          <p>
            On this page you can manage your premium servers. You have
            {{ boosts_left }} slots available from total of
            {{ tier + entitled_tier }} slots
          </p>
          <b-row align-h="center">
            <div v-for="g in sorted_guilds" :key="g.id" class="row">
              <b-col class="m-2 center">
                <span @click="open_modal(g)">
                  <VueImage
                    :src="g.icon"
                    :styles="get_style(g)"
                    :title="g.link ? 'Not yet invited, click to invite' : ''"
                    height="128"
                    width="128"
                  ></VueImage>
                </span>
                <p class="text-center">{{ g.name }}</p>
              </b-col>
            </div>
          </b-row>
        </div>
      </div>
    </b-card>
    <b-modal
      :title="modaltitle"
      size="md"
      v-model="modal"
      :no-enforce-focus="true"
    >
      <div v-if="selected_guild.vip">
        <p>
          Click the button below to remove premium from this server. Please keep
          in mind this removes any temporary reaction roles.
        </p>
      </div>
      <div v-else>
        <p>
          Press the button below to immediately gain access to all premium
          features.
        </p>
      </div>
      <div slot="modal-footer" class="w-100">
        <b-container
          fluid
          class="text-light text-center"
          style="padding-right:0px;"
        >
          <b-row class="mr-auto">
            <b-col cols="auto" class="mr-auto p-2"></b-col>
            <b-col cols="auto" class="p-2">
              <b-button variant="ghost-secondary" v-on:click="modal = false"
                >Cancel</b-button
              >
            </b-col>
            <b-col cols="auto" class="p-2">
              <b-button
                v-if="selected_guild.vip"
                variant="danger"
                v-on:click="remove_premium"
                >Remove</b-button
              >
              <template v-else>
                <b-button
                  v-if="is_patron"
                  variant="success"
                  v-on:click="add_premium('patreon')"
                  style="margin-right: 8px;"
                  :disabled="!isPatreonSlotAvailable"
                >
                  Use Patreon Slot
                </b-button>

                <b-button
                  v-if="is_entitled"
                  variant="success"
                  v-on:click="add_premium('entitlement')"
                  :disabled="!isDiscordSlotAvailable"
                >
                  Use Discord Slot
                </b-button>
              </template>
            </b-col>
          </b-row>
        </b-container>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from "vue";
import VueNotifications from "vue-notifications";
import miniToastr from "mini-toastr";
import VueImage from "@/components/VueImage";
import features from "./premium-features.json";
import DiscordPremiumButton from "../../components/DiscordPremiumButton.vue";
import PatreonPremiumButton from "../../components/PatreonPremiumButton.vue";

const toastTypes = {
  success: "success",
  error: "error",
  info: "info",
  warn: "warn"
};
miniToastr.init({ types: toastTypes });

function toast({ title, message, type, timeout, cb }) {
  return miniToastr[type](message, title, timeout, cb);
}

const toast_options = {
  success: toast,
  error: toast,
  info: toast,
  warn: toast
};
Vue.use(VueNotifications, toast_options);

export default {
  name: "ServerPicker",
  components: {
    VueImage,
    DiscordPremiumButton,
    PatreonPremiumButton
  },
  data: function() {
    return {
      features,
      is_patron: false,
      is_entitled: false,
      vip_servers: [],
      tier: 0,
      entitled_tier: 0,
      boosts_left: 0,
      selected_guild: {},
      modal: false,
      loading: true,
      discord_slots: 0,
      patreon_slots: 0
    };
  },
  notifications: {
    alert: {
      type: VueNotifications.types.warn,
      title: "Sorry!",
      message: "You do not have any boosts left"
    },
    success: {
      type: VueNotifications.types.success,
      title: "Success!",
      message: "You will be redirected to your server's dashboard in a second"
    }
  },
  methods: {
    get_style(g) {
      const styles = "cursor: pointer; border-radius: 50%; ";
      if (g.link === undefined && g.vip) {
        return styles + "border: 3px solid #ff675a";
      }
      return g.link === undefined
        ? styles + "border: 3px solid #6AFFAA;"
        : styles +
            "border: 3px solid #6AFFAA; -webkit-filter: grayscale(100%); -moz-filter: grayscale(100%); -ms-filter: grayscale(100%); -o-filter: grayscale(100%); filter: grayscale(100%);";
    },
    open_modal(g) {
      if (!g.vip && this.boosts_left == 0) {
        return this.alert();
      }
      this.selected_guild = g;
      this.modal = true;
    },
    add_premium(premium_type) {
      if (!this.selected_guild.id) {
        return this.alert({
          message:
            "Not sure how you ended up in this situation but I don't know what server you want to boost."
        });
      }
      return this.axios
        .post("/api/v1/servers/" + this.selected_guild.id + "/patron", {
          premium_type: premium_type
        })
        .then(() => {
          this.success();
          this.$store.commit("toggle_premium", this.selected_guild.id);
          setTimeout(() => {
            this.$router.push(
              "/dashboard/" + this.selected_guild.id + "/botsettings"
            );
          }, 1000);
        });
    },
    remove_premium() {
      if (!this.selected_guild.id) {
        return this.alert({
          message:
            "Not sure how you ended up in this situation but I don't know what server you want to boost."
        });
      }
      return this.axios
        .delete("/api/v1/servers/" + this.selected_guild.id + "/patron")
        .then(() => {
          this.success();
          this.$store.commit("toggle_premium", this.selected_guild.id);
          setTimeout(() => {
            this.$router.push(
              "/dashboard/" + this.selected_guild.id + "/botsettings"
            );
          }, 1000);
        });
    }
  },

  computed: {
    sorted_guilds() {
      return this.$store.state.discord_guilds
        .filter(g => !g.link)
        .sort(
          (a, b) =>
            (a.link || "").length - (b.link || "").length || a.name > b.name
        );
    },
    modaltitle() {
      return this.selected_guild.vip
        ? "Remove Premium From Server " + this.selected_guild.name
        : "Add Premium to " + this.selected_guild.name;
    },
    isPatreonSlotAvailable() {
      return this.patreon_slots > 0 || false;
    },
    isDiscordSlotAvailable() {
      return this.discord_slots > 0 || false;
    }
  },
  created: function() {
    this.axios.get("/api/v1/users/@me/guilds").then(r => {
      this.$store.commit("login", r.data.user);
      this.$store.commit("sync_guilds", r.data.guilds);
      this.loading = false;
      //this.servers = r.data.guilds
    });

    this.axios.get("/api/v1/users/@me/premium").then(r => {
      this.loading = false;
      this.vip_servers = r.data.servers;
      this.is_patron = r.data.is_patron;
      this.is_entitled = r.data.is_entitled;
      this.tier = r.data.tier;
      this.entitled_tier = r.data.entitled_tier;
      this.boosts_left =
        r.data.tier + r.data.entitled_tier - r.data.servers.length;
      this.discord_slots = r.data.available_discord;
      this.patreon_slots = r.data.available_patreon;
    });
  }
};
</script>
<style src="spinkit/spinkit.min.css"></style>
<style>
.premium-features-list {
  padding-left: 1.2rem;
  padding-right: 1.2rem;
  margin: 0 auto;
  max-width: 100%;
  font-size: 1rem;
  line-height: 1.5;
  word-break: break-word;
}
.premium-features-list li {
  margin-bottom: 0.75em;
}
@media (max-width: 575.98px) {
  .premium-features-list {
    font-size: 0.95rem;
    padding-left: 0.8rem;
    padding-right: 0.8rem;
  }
}
</style>
